import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import LeadCaptureFlow from "@/components/starter/LeadCaptureFlow";
import { ArrowRight } from "lucide-react";
import { useBrickTranslation } from "@/hooks/useBrickTranslation";

interface ContactModalProps {
  children?: React.ReactNode;
  triggerLabel?: string;
  triggerVariant?: "default" | "premium" | "outline" | "ghost";
  triggerSize?: "default" | "sm" | "lg";
  triggerClassName?: string;
  modalTitle?: string;
  modalSubtitle?: string;
  /**
   * Controlled mode — when `headless` is true, no trigger is rendered and the
   * modal opens/closes purely via `open` / `onOpenChange`. Used by NavBar to
   * open the same ContactModal from menu items without a visible trigger.
   */
  headless?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const ContactModal = ({
  children,
  triggerLabel,
  triggerVariant = "premium",
  triggerSize = "lg",
  triggerClassName = "rounded-full group",
  modalTitle,
  modalSubtitle,
  headless = false,
  open: controlledOpen,
  onOpenChange,
}: ContactModalProps) => {
  const { t } = useBrickTranslation('common');
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen! : internalOpen;
  const setOpen = (v: boolean) => {
    if (!isControlled) setInternalOpen(v);
    onOpenChange?.(v);
  };

  const label = triggerLabel || t('cta.contact');
  const title = modalTitle || t('contactChat.modalTitle');
  const subtitle = modalSubtitle || t('contactChat.modalSubtitle');

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!headless && (
        <DialogTrigger asChild>
          {children || (
            <Button variant={triggerVariant} size={triggerSize} className={triggerClassName}>
              {label}
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto bg-[hsl(var(--bg-canvas))] border-[hsl(var(--border-hairline))] p-0">
        <div className="relative z-10 p-6 pt-8">
          <div className="text-center mb-6">
            <DialogTitle asChild>
              <h2 className="font-black text-xl sm:text-2xl text-[hsl(var(--fg-cream))] mb-1 tracking-tight">
                {title}
              </h2>
            </DialogTitle>
            <DialogDescription className="text-sm text-[hsl(var(--fg-muted))]">
              {subtitle}
            </DialogDescription>
          </div>
          <LeadCaptureFlow variant="modal" />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
